import React from 'react';
import { Notebook, X, Edit2, Trash2, Send } from 'lucide-react';
import { Note } from '../services/apiService';

interface NotesPaneProps {
  isOpen: boolean;
  onClose: () => void;
  notes: Note[];
  loading: boolean;
  currentUserName?: string;
  // Create
  newNoteText: string;
  setNewNoteText: (val: string) => void;
  onCreateNote: () => void;
  // Update
  editingNoteId: string | number | null;
  setEditingNoteId: (id: string | number | null) => void;
  editingNoteText: string;
  setEditingNoteText: (val: string) => void;
  onUpdateNote: (note: Note) => void;
  // Delete
  onDeleteNote: (note: Note) => void;
}

export const NotesPane: React.FC<NotesPaneProps> = ({
  isOpen,
  onClose,
  notes,
  loading,
  currentUserName,
  newNoteText,
  setNewNoteText,
  onCreateNote,
  editingNoteId,
  setEditingNoteId,
  editingNoteText,
  setEditingNoteText,
  onUpdateNote,
  onDeleteNote,
}) => {
  if (!isOpen) return null;

  return (
    <div className="absolute top-0 right-0 bottom-0 w-80 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl z-30 flex flex-col animate-in slide-in-from-right duration-200">
      {/* Header */}
      <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/20">
        <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Notebook size={16} className="text-primary" />
          Notes
        </h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center py-8 text-slate-400 text-xs">Loading notes...</div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-xs italic">No notes yet.</div>
        ) : (
          notes.map((note) => {
            const author = note.author || "Unknown";
            const created = note.createdDateTime;
            const content = note.note || "";
            const isAuthor = currentUserName === author;
            const isEditing = editingNoteId === note.id;

            return (
              <div key={note.id} className="group relative bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{author}</span>
                  <span className="text-[10px] text-slate-400">
                    {created ? new Date(created).toLocaleDateString() : "N/A"}
                  </span>
                </div>

                {isEditing ? (
                  <div className="mt-2">
                    <textarea
                      value={editingNoteText}
                      onChange={(e) => setEditingNoteText(e.target.value)}
                      maxLength={280}
                      className="w-full text-xs p-2 dark:text-slate-300 rounded border border-primary bg-white dark:bg-slate-900 focus:outline-none resize-none"
                      rows={3}
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button onClick={() => setEditingNoteId(null)} className="text-[10px] font-bold text-slate-500">Cancel</button>
                      <button onClick={() => onUpdateNote(note)} className="text-[10px] font-bold text-primary">Save</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap break-words">{content}</p>
                )}

                {isAuthor && !isEditing && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-50 dark:bg-slate-800 p-1 rounded">
                    <button onClick={() => { setEditingNoteId(note.id); setEditingNoteText(content); }} className="p-1 text-slate-400 hover:text-primary"><Edit2 size={12} /></button>
                    <button onClick={() => onDeleteNote(note)} className="p-1 text-slate-400 hover:text-rose-500"><Trash2 size={12} /></button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer / Input */}
      <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/20">
        <div className="relative">
          <textarea
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            placeholder="Add a note..."
            maxLength={280}
            className="w-full text-xs p-3 pr-10 rounded-xl dark:text-slate-300 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary/50 outline-none resize-none"
            rows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onCreateNote();
              }
            }}
          />
          <button
            onClick={onCreateNote}
            disabled={!newNoteText.trim()}
            className="absolute right-2 bottom-2 p-1.5 bg-primary text-white rounded-lg disabled:opacity-50"
          >
            <Send size={14} />
          </button>
        </div>
        <div className="text-[10px] text-slate-400 text-right mt-1">{newNoteText.length}/280</div>
      </div>
    </div>
  );
};